"use client"

import { useApp } from "../contexts/AppContext"
import { useState, useEffect, useMemo } from "react"
import { BiPlus, BiFolder, BiArrowBack, BiSearch, BiSortAlt2, BiX, BiRefresh } from "react-icons/bi"
import { useParams, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"

import ClasseurModal from "../components/modals/ClasseurModal"
import ClasseurCard from "../components/cards/ClasseurCard"
import AnimatedLoader from "../components/ui/AnimatedLoader"
import HeaderAlert from "../components/ui/HeaderAlert"

export default function ClasseurPage() {
  const navigate = useNavigate()

  const { projectId } = useParams()
  const { getProjet, getClasseursByProject, error, success, clearError, clearSuccess, loading } = useApp()

  const [showModal, setShowModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortBy, setSortBy] = useState("nom_classeur")
  const [sortOrder, setSortOrder] = useState("asc")
  const [projetClasseurs, setProjetClasseurs] = useState([])

  // Données
  const projet = getProjet(projectId)
  useEffect(() => {
    const allClasseurs = getClasseursByProject(projectId)
    if (allClasseurs) {
      setProjetClasseurs(allClasseurs)
    }
  }, [projectId, getClasseursByProject])

  // Classeurs filtrées et triées
  const filteredAndSortedClasseurs = useMemo(() => {
    const filtered = projetClasseurs.filter((classeur) =>
      classeur.libelle_classeur.toLowerCase().includes(searchTerm.toLowerCase()) ||
      classeur.note_classeur.toLowerCase().includes(searchTerm.toLowerCase()))

    filtered.sort((a, b) => {
      let aValue = a.libelle_classeur || ""
      let bValue = b.libelle_classeur || ""

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })

    return filtered
  }, [projetClasseurs, searchTerm, sortBy, sortOrder])

  //=======================================================>
  //=======================================================>
  // Variants pour les animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    },
    exit: {
      y: -20,
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.2,
      },
    },
  }

  const headerVariants = {
    hidden: { y: -50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
  }

  const emptyStateVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 20,
      },
    },
  }

  if (!projet && !loading) {
    return (
      <div className="m-3 p-3 border shadow-sm rounded-1 bg-white" style={{ minHeight: "95vh" }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-5"
        >
          <BiFolder className="display-1 text-danger mb-3" />
          <h3 className="text-danger">Projet non trouvé</h3>
          <p className="text-muted mb-4">Le projet demandé n'existe pas ou a été supprimé.</p>
          <button onClick={() => navigate("/")} className="btn btn-primary">
            <BiArrowBack className="me-1" />
            Retour à l'accueil
          </button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="m-3 p-3 border shadow-sm rounded-1 bg-white" style={{ minHeight: "95vh" }}>
      {/* Messages d'alerte avec animations */}
      <HeaderAlert error={error} success={success} clearError={clearError} clearSuccess={clearSuccess} />

      {/* En-tête avec animation */}
      <motion.div
        variants={headerVariants}
        initial="hidden"
        animate="visible"
        className="d-flex justify-content-between align-items-start mb-3"
      >
        <div className="d-flex align-items-start">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <button onClick={() => navigate("/")} className="btn btn-sm btn-outline-secondary me-3 d-flex align-items-center">
              <BiArrowBack className="me-1" /> Retour
            </button>
          </motion.div>

          <div>
            <motion.h5
              className="mb-0"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {projet ? projet.nom || projet.name : `Projet ${projectId}`}
            </motion.h5>
            <motion.small
              className="text-muted"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {filteredAndSortedClasseurs.length} classeur{filteredAndSortedClasseurs.length !== 1 ? "s" : ""}
            </motion.small>
          </div>
        </div>

        {/* Rafraichir la page */}
        <div className="d-flex align-items-center gap-2">
          <motion.button
            onClick={() => window.location.reload()}
            title="Rafraichir"
            className="btn btn-sm btn-outline-secondary d-flex align-items-center p-1 rounded-circle"
            disabled={loading}>
            <BiRefresh className="fs-5" />
          </motion.button>

          <motion.button
            onClick={() => setShowModal(true)}
            className="btn btn-sm btn-primary d-flex align-items-center"
            disabled={loading}>
            <BiPlus className="me-1" />
            {loading ? "Chargement..." : "Nouveau Classeur"}
          </motion.button>
        </div>
      </motion.div>

      {/* Barre de recherche et filtres */}
      <motion.div
        className="row g-3 mb-3"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <div className="col-md-6">
          <div className="input-group input-group-sm">
            <span className="input-group-text">
              <BiSearch />
            </span>
            <input
              id="search-input"
              type="text"
              className="form-control"
              placeholder="Rechercher un classeur... (Ctrl+F)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && <button className="btn btn-outline-secondary" onClick={() => setSearchTerm("")}><BiX /></button>}
          </div>
        </div>

        <div className="col-md-3">
          <select className="form-select form-select-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="libelle_classeur">Trier par libellé</option>
            <option value="note_classeur">Trier par description</option>
            <option value="date_insertion">Trier par date</option>
          </select>
        </div>

        <div className="col-md-3">
          <button className="btn btn-sm btn-outline-secondary w-100 d-flex align-items-center justify-content-center"
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}>
            <BiSortAlt2 className="me-1" />
            {sortOrder === "asc" ? "Croissant" : "Décroissant"}
          </button>
        </div>
      </motion.div>

      {/* Contenu principal avec animations */}
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        <AnimatePresence mode="wait">
          {loading && filteredAndSortedClasseurs.length === 0 ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <AnimatedLoader text="Chargement des classeurs..." />
            </motion.div>
          ) : filteredAndSortedClasseurs.length === 0 ? (
            <motion.div
              key="empty"
              variants={emptyStateVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="d-flex flex-column align-items-center py-5 justify-content-center"
            >
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Number.POSITIVE_INFINITY,
                  repeatType: "reverse",
                }}
              >
                <BiFolder className="display-1 text-muted" />
              </motion.div>
              <motion.h4
                className="text-muted mt-3"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                Aucun classeur
              </motion.h4>

              <motion.p
                className="text-muted"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Créez votre premier classeur pour commencer
              </motion.p>

              <motion.button
                onClick={() => setShowModal(true)}
                className="btn btn-sm btn-primary d-flex align-items-center"
                disabled={loading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <BiPlus className="me-1" /> Créer un classeur
              </motion.button>
            </motion.div>
          ) : (
            <motion.div key="classeurs" variants={containerVariants} initial="hidden" animate="visible" className="row">
              <AnimatePresence>
                {filteredAndSortedClasseurs.map((classeur, index) => (
                  <motion.div
                    key={classeur.code_classeur}
                    variants={itemVariants}
                    className="col-md-6 col-lg-4 col-xl-3 mb-4"
                    style={{ zIndex: filteredAndSortedClasseurs.length - index }}
                  >
                    <ClasseurCard classeur={classeur} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Modal avec animation */}
      <ClasseurModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        projectId={projectId}
      />
    </div>
  )
}
